import {
  get,
  onValue,
  push,
  query,
  ref,
  runTransaction,
  set,
  update,
  orderByChild,
  equalTo,
  type Unsubscribe,
} from 'firebase/database';
import { getFirebaseDb } from '@/lib/firebase';
import { ensureAnonymousAuth, upsertCustomerProfile } from '@/services/authService';
import { sendChatMessage } from '@/services/chatService';
import { notifyAgentsOfRequest } from '@/services/emailService';
import { pushNotification, showBrowserNotification, playNotificationSound } from '@/services/notificationService';
import { generateAccessToken, getBrowserInfo, summarizeIssue } from '@/services/supportUtils';
import {
  DEFAULT_SUPPORT_SETTINGS,
  type AiHistoryItem,
  type ChatHistoryRecord,
  type ChatMessage,
  type Priority,
  type SupportAgent,
  type SupportRequest,
  type SupportSettings,
  type WorkingHours,
} from '@/types/support';

export async function listActiveAgents(): Promise<SupportAgent[]> {
  const snap = await get(ref(getFirebaseDb(), 'agents'));
  if (!snap.exists()) return [];
  const val = snap.val() as Record<string, Omit<SupportAgent, 'agentId'>>;
  return Object.entries(val)
    .map(([agentId, a]) => ({ agentId, ...a }))
    .filter((a) => a.active);
}

export async function listAllAgents(): Promise<SupportAgent[]> {
  const snap = await get(ref(getFirebaseDb(), 'agents'));
  if (!snap.exists()) return [];
  const val = snap.val() as Record<string, Omit<SupportAgent, 'agentId'>>;
  return Object.entries(val).map(([agentId, a]) => ({ agentId, ...a }));
}

export function subscribeAgents(cb: (agents: SupportAgent[]) => void): Unsubscribe {
  return onValue(ref(getFirebaseDb(), 'agents'), (snap) => {
    if (!snap.exists()) {
      cb([]);
      return;
    }
    const val = snap.val() as Record<string, Omit<SupportAgent, 'agentId'>>;
    cb(Object.entries(val).map(([agentId, a]) => ({ agentId, ...a })));
  });
}

export async function getSupportSettings(): Promise<SupportSettings> {
  const snap = await get(ref(getFirebaseDb(), 'settings/support'));
  if (!snap.exists()) return DEFAULT_SUPPORT_SETTINGS;
  return { ...DEFAULT_SUPPORT_SETTINGS, ...snap.val() };
}

/**
 * Customers cannot list all supportRequests (security rules).
 * Use a shared counter agents/customers can update, with a safe fallback.
 */
async function nextQueuePosition(): Promise<number> {
  const counterRef = ref(getFirebaseDb(), 'settings/support/waitingCount');
  try {
    const result = await runTransaction(counterRef, (current) => {
      const n = typeof current === 'number' ? current : 0;
      return n + 1;
    });
    if (result.committed && typeof result.snapshot.val() === 'number') {
      return result.snapshot.val() as number;
    }
  } catch {
    // Rules may not allow counter yet - fall back
  }
  return 1;
}

export async function createSupportRequest(opts: {
  customerName?: string | null;
  aiHistory: AiHistoryItem[];
  issueSummary?: string;
  priority?: Priority;
}): Promise<{ request: SupportRequest; accessToken: string }> {
  const user = await ensureAnonymousAuth();
  const db = getFirebaseDb();
  const settings = await getSupportSettings();
  const queuePosition = await nextQueuePosition();

  const requestRef = push(ref(db, 'supportRequests'));
  const requestId = requestRef.key!;
  const accessToken = generateAccessToken();
  const now = Date.now();

  // Omit null fields - RTDB strips nulls; rules check assignedAgent == null via missing child
  const request: SupportRequest = {
    requestId,
    customerId: user.uid,
    customerName: opts.customerName ?? null,
    anonymousUserId: user.uid,
    browserInfo: getBrowserInfo(),
    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    createdAt: now,
    status: 'waiting',
    assignedAgent: null,
    acceptedAt: null,
    aiHistory: opts.aiHistory,
    issueSummary: summarizeIssue(opts.aiHistory, opts.issueSummary),
    priority: opts.priority ?? 'normal',
    source: 'chatbot',
    queuePosition,
    estimatedWaitSec: queuePosition * settings.estimatedWaitPerPositionSec,
  };

  const requestPayload = {
    requestId,
    customerId: user.uid,
    customerName: opts.customerName ?? null,
    anonymousUserId: user.uid,
    browserInfo: request.browserInfo,
    pageUrl: request.pageUrl,
    createdAt: now,
    status: 'waiting' as const,
    assignedAgent: null,
    aiHistory: opts.aiHistory,
    issueSummary: request.issueSummary,
    priority: request.priority,
    source: 'chatbot' as const,
    queuePosition,
    estimatedWaitSec: request.estimatedWaitSec,
  };

  await set(requestRef, requestPayload);
  await set(ref(db, `supportRequestSecrets/${requestId}`), {
    accessToken,
    createdAt: now,
  });
  await upsertCustomerProfile(user.uid, {
    displayName: opts.customerName,
    lastPageUrl: request.pageUrl,
  });

  // Non-critical: never block handoff if notify paths fail rules/email
  try {
    const agents = await listActiveAgents();
    for (const agent of agents) {
      try {
        await pushNotification(agent.agentId, {
          type: 'new_request',
          title: 'New support request',
          body: request.issueSummary,
          requestId,
        });
      } catch {
        // ignore per-agent notification failures
      }
    }
    await notifyAgentsOfRequest(request, accessToken, agents);
  } catch {
    // Email / agent list failures must not block handoff
  }

  return { request, accessToken };
}

export function subscribeSupportRequest(
  requestId: string,
  cb: (request: SupportRequest | null) => void,
): Unsubscribe {
  return onValue(ref(getFirebaseDb(), `supportRequests/${requestId}`), (snap) => {
    if (!snap.exists()) {
      cb(null);
      return;
    }
    cb({ requestId, ...snap.val() } as SupportRequest);
  });
}

export function subscribeWaitingRequests(cb: (requests: SupportRequest[]) => void): Unsubscribe {
  const q = query(ref(getFirebaseDb(), 'supportRequests'), orderByChild('status'), equalTo('waiting'));
  return onValue(q, (snap) => {
    if (!snap.exists()) {
      cb([]);
      return;
    }
    const val = snap.val() as Record<string, SupportRequest>;
    cb(
      Object.entries(val)
        .map(([requestId, r]) => ({ ...r, requestId }))
        .sort((a, b) => a.createdAt - b.createdAt),
    );
  });
}

export function subscribeAgentActiveChats(
  agentId: string,
  cb: (chats: SupportRequest[]) => void,
): Unsubscribe {
  const q = query(ref(getFirebaseDb(), 'supportRequests'), orderByChild('assignedAgent'), equalTo(agentId));
  return onValue(q, (snap) => {
    if (!snap.exists()) {
      cb([]);
      return;
    }
    const val = snap.val() as Record<string, SupportRequest>;
    cb(
      Object.entries(val)
        .map(([requestId, r]) => ({ ...r, requestId }))
        .filter((r) => r.status === 'accepted')
        .sort((a, b) => (b.acceptedAt ?? 0) - (a.acceptedAt ?? 0)),
    );
  });
}

async function verifyAccessToken(requestId: string, token: string): Promise<boolean> {
  const snap = await get(ref(getFirebaseDb(), `supportRequestSecrets/${requestId}/accessToken`));
  return snap.exists() && snap.val() === token;
}

export async function acceptSupportRequest(opts: {
  requestId: string;
  agentId: string;
  accessToken: string;
  agentName: string;
}): Promise<{ ok: true } | { ok: false; reason: 'already_assigned' | 'invalid_token' | 'not_waiting' }> {
  const valid = await verifyAccessToken(opts.requestId, opts.accessToken);
  if (!valid) return { ok: false, reason: 'invalid_token' };

  const requestRef = ref(getFirebaseDb(), `supportRequests/${opts.requestId}`);
  const result = await runTransaction(requestRef, (current) => {
    if (!current) return current;
    if (current.status !== 'waiting') return;
    return {
      ...current,
      status: 'accepted',
      assignedAgent: opts.agentId,
      acceptedAt: Date.now(),
    };
  });

  if (!result.committed) {
    const snap = await get(requestRef);
    const current = snap.val() as SupportRequest | null;
    if (current?.assignedAgent && current.assignedAgent !== opts.agentId) {
      return { ok: false, reason: 'already_assigned' };
    }
    return { ok: false, reason: 'not_waiting' };
  }

  const request = result.snapshot.val() as SupportRequest;
  const now = Date.now();

  await set(ref(getFirebaseDb(), `activeChats/${opts.requestId}`), {
    requestId: opts.requestId,
    customerId: request.customerId,
    agentId: opts.agentId,
    startedAt: now,
    status: 'active',
    lastMessageAt: now,
    unreadCustomer: 0,
    unreadAgent: 0,
  });

  await sendChatMessage({
    requestId: opts.requestId,
    senderId: opts.agentId,
    senderRole: 'system',
    text: `${opts.agentName} joined the conversation.`,
    type: 'system',
  });

  await pushNotification(request.customerId, {
    type: 'request_accepted',
    title: 'Agent joined',
    body: `${opts.agentName} joined the conversation.`,
    requestId: opts.requestId,
  });

  return { ok: true };
}

export async function rejectSupportRequest(opts: {
  requestId: string;
  agentId: string;
  accessToken: string;
}): Promise<{ ok: true } | { ok: false; reason: string }> {
  const valid = await verifyAccessToken(opts.requestId, opts.accessToken);
  if (!valid) return { ok: false, reason: 'invalid_token' };

  const requestRef = ref(getFirebaseDb(), `supportRequests/${opts.requestId}`);
  const result = await runTransaction(requestRef, (current) => {
    if (!current) return current;
    if (current.status !== 'waiting') return;
    return {
      ...current,
      status: 'rejected',
      rejectedBy: opts.agentId,
    };
  });

  if (!result.committed) return { ok: false, reason: 'already_handled' };
  return { ok: true };
}

export async function markNeedMoreInfo(opts: {
  requestId: string;
  agentId: string;
  accessToken: string;
  note?: string;
}): Promise<{ ok: true } | { ok: false; reason: string }> {
  const valid = await verifyAccessToken(opts.requestId, opts.accessToken);
  if (!valid) return { ok: false, reason: 'invalid_token' };

  await update(ref(getFirebaseDb(), `supportRequests/${opts.requestId}`), {
    status: 'info_needed',
    transferNote: opts.note ?? null,
  });
  return { ok: true };
}

export async function transferSupportRequest(opts: {
  requestId: string;
  fromAgentId: string;
  toAgentId: string;
  note?: string;
}): Promise<void> {
  const requestRef = ref(getFirebaseDb(), `supportRequests/${opts.requestId}`);
  const snap = await get(requestRef);
  if (!snap.exists()) throw new Error('Request not found');
  const request = snap.val() as SupportRequest;
  if (request.assignedAgent !== opts.fromAgentId) throw new Error('Only the assigned agent can transfer');

  await update(requestRef, {
    assignedAgent: opts.toAgentId,
    transferNote: opts.note ?? null,
    status: 'accepted',
  });
  await update(ref(getFirebaseDb(), `activeChats/${opts.requestId}`), {
    agentId: opts.toAgentId,
  });

  const toAgent = (await get(ref(getFirebaseDb(), `agents/${opts.toAgentId}`))).val() as SupportAgent | null;
  await sendChatMessage({
    requestId: opts.requestId,
    senderId: opts.fromAgentId,
    senderRole: 'system',
    text: `Conversation transferred to ${toAgent?.name ?? 'another agent'}.`,
    type: 'system',
  });

  await pushNotification(opts.toAgentId, {
    type: 'transfer',
    title: 'Chat transferred to you',
    body: request.issueSummary,
    requestId: opts.requestId,
  });
  showBrowserNotification('Chat transferred', request.issueSummary, opts.requestId);
  playNotificationSound();
}

export async function cancelSupportRequest(requestId: string, customerId: string): Promise<void> {
  const requestRef = ref(getFirebaseDb(), `supportRequests/${requestId}`);
  const snap = await get(requestRef);
  if (!snap.exists()) return;
  const request = snap.val() as SupportRequest;
  if (request.customerId !== customerId) throw new Error('Not your request');
  if (request.status !== 'waiting') throw new Error('Request can no longer be cancelled');
  await update(requestRef, { status: 'cancelled' });
}

export async function closeSupportConversation(opts: {
  requestId: string;
  agentId: string;
  agentName: string;
}): Promise<void> {
  const db = getFirebaseDb();
  const requestSnap = await get(ref(db, `supportRequests/${opts.requestId}`));
  if (!requestSnap.exists()) throw new Error('Request not found');
  const request = { requestId: opts.requestId, ...requestSnap.val() } as SupportRequest;
  if (request.assignedAgent !== opts.agentId) throw new Error('Not assigned to you');

  const messagesSnap = await get(ref(db, `chatMessages/${opts.requestId}`));
  const messageList: ChatMessage[] = messagesSnap.exists()
    ? Object.values(messagesSnap.val() as Record<string, ChatMessage>).sort((a, b) => a.createdAt - b.createdAt)
    : [];

  // Store as map (not JS array) - RTDB arrays are fragile and can break writes
  const messagesMap: Record<string, ChatMessage> = {};
  messageList.forEach((m, i) => {
    const id = m.messageId || `m${i}`;
    messagesMap[id] = {
      messageId: id,
      senderId: m.senderId || '',
      senderRole: m.senderRole || 'system',
      text: m.text || '',
      createdAt: typeof m.createdAt === 'number' ? m.createdAt : Date.now(),
      delivered: m.delivered !== false,
      read: Boolean(m.read),
      type: m.type === 'system' ? 'system' : 'text',
    };
  });

  const endedAt = Date.now();
  const startedAt = request.acceptedAt ?? request.createdAt;

  // No null fields - RTDB strips nulls; keep payload plain JSON-safe
  const historyPayload: Record<string, unknown> = {
    requestId: opts.requestId,
    customerId: request.customerId,
    customerName: request.customerName || 'Anonymous visitor',
    agentId: opts.agentId,
    agentName: opts.agentName || 'Support',
    startedAt,
    endedAt,
    durationSec: Math.max(0, Math.round((endedAt - startedAt) / 1000)),
    messages: messagesMap,
    status: 'closed',
    issueSummary: request.issueSummary || 'Support conversation',
    pageUrl: request.pageUrl || '',
  };

  try {
    await set(ref(db, `chatHistory/${opts.requestId}`), historyPayload);
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to save chat history';
    throw new Error(`Could not save history: ${msg}`);
  }

  await update(ref(db, `supportRequests/${opts.requestId}`), {
    status: 'closed',
    closedAt: endedAt,
  });

  try {
    await update(ref(db, `activeChats/${opts.requestId}`), { status: 'closed' });
  } catch {
    // optional
  }

  try {
    await sendChatMessage({
      requestId: opts.requestId,
      senderId: opts.agentId,
      senderRole: 'system',
      text: 'This conversation has been closed by support.',
      type: 'system',
    });
  } catch {
    // history already saved - don't fail close
  }

  try {
    await pushNotification(request.customerId, {
      type: 'request_closed',
      title: 'Conversation closed',
      body: 'Your support conversation has ended.',
      requestId: opts.requestId,
    });
  } catch {
    // optional
  }
}

export async function getRequestWithSecret(requestId: string): Promise<{
  request: SupportRequest;
  accessToken: string | null;
} | null> {
  const [reqSnap, secretSnap] = await Promise.all([
    get(ref(getFirebaseDb(), `supportRequests/${requestId}`)),
    get(ref(getFirebaseDb(), `supportRequestSecrets/${requestId}`)),
  ]);
  if (!reqSnap.exists()) return null;
  return {
    request: { requestId, ...reqSnap.val() } as SupportRequest,
    accessToken: secretSnap.exists() ? (secretSnap.val().accessToken as string) : null,
  };
}

export async function saveAgent(
  agentId: string,
  data: Partial<SupportAgent> & { name: string; email: string; role: SupportAgent['role']; active: boolean },
): Promise<void> {
  const existing = await get(ref(getFirebaseDb(), `agents/${agentId}`));
  const now = Date.now();
  const prev = existing.exists() ? (existing.val() as SupportAgent) : null;
  const payload: Omit<SupportAgent, 'agentId'> = {
    name: data.name,
    email: data.email,
    role: data.role,
    active: data.active,
    profileImage: data.profileImage ?? prev?.profileImage ?? null,
    phone: data.phone ?? prev?.phone ?? null,
    workingHours: (data.workingHours as WorkingHours | null | undefined) ?? prev?.workingHours ?? null,
    maxConcurrentChats: data.maxConcurrentChats ?? prev?.maxConcurrentChats ?? 3,
    presenceStatus: data.presenceStatus ?? prev?.presenceStatus ?? 'offline',
    createdAt: prev?.createdAt ?? now,
    updatedAt: now,
  };
  await set(ref(getFirebaseDb(), `agents/${agentId}`), payload);
}

export async function deleteAgent(agentId: string): Promise<void> {
  await set(ref(getFirebaseDb(), `agents/${agentId}`), null);
}

export async function setAgentActive(agentId: string, active: boolean): Promise<void> {
  await update(ref(getFirebaseDb(), `agents/${agentId}`), { active, updatedAt: Date.now() });
}

function normalizeHistoryRecord(requestId: string, raw: Record<string, unknown>): ChatHistoryRecord {
  const messagesRaw = raw.messages;
  let messages: ChatMessage[] = [];
  if (Array.isArray(messagesRaw)) {
    messages = messagesRaw as ChatMessage[];
  } else if (messagesRaw && typeof messagesRaw === 'object') {
    messages = Object.values(messagesRaw as Record<string, ChatMessage>).sort(
      (a, b) => (a.createdAt || 0) - (b.createdAt || 0),
    );
  }

  return {
    requestId: (raw.requestId as string) || requestId,
    customerId: (raw.customerId as string) || '',
    customerName: (raw.customerName as string) || 'Anonymous visitor',
    agentId: (raw.agentId as string) || '',
    agentName: (raw.agentName as string) || 'Support',
    startedAt: Number(raw.startedAt) || 0,
    endedAt: Number(raw.endedAt) || 0,
    durationSec: Number(raw.durationSec) || 0,
    messages,
    status: 'closed',
    issueSummary: (raw.issueSummary as string) || '',
    pageUrl: (raw.pageUrl as string) || '',
  };
}

export function subscribeChatHistory(
  cb: (rows: ChatHistoryRecord[]) => void,
  onError?: (message: string) => void,
): Unsubscribe {
  return onValue(
    ref(getFirebaseDb(), 'chatHistory'),
    (snap) => {
      if (!snap.exists()) {
        cb([]);
        return;
      }
      const val = snap.val() as Record<string, Record<string, unknown>>;
      const rows = Object.entries(val)
        .map(([id, raw]) => normalizeHistoryRecord(id, raw || {}))
        .sort((a, b) => b.endedAt - a.endedAt);
      cb(rows);
    },
    (err) => {
      onError?.(err.message || 'Failed to load chat history');
      cb([]);
    },
  );
}

/** Deletes history + related chat data from Realtime Database */
export async function deleteChatHistory(requestId: string): Promise<void> {
  const db = getFirebaseDb();
  const paths = [
    `chatHistory/${requestId}`,
    `chatMessages/${requestId}`,
    `activeChats/${requestId}`,
    `supportRequestSecrets/${requestId}`,
    `typing/${requestId}`,
    `supportRequests/${requestId}`,
  ];
  await Promise.all(paths.map((p) => set(ref(db, p), null)));
}

export async function deleteChatHistoryBulk(requestIds: string[]): Promise<void> {
  for (const id of requestIds) {
    await deleteChatHistory(id);
  }
}

export function exportHistoryJson(rows: ChatHistoryRecord[]): string {
  return JSON.stringify(rows, null, 2);
}

export function exportHistoryCsv(rows: ChatHistoryRecord[]): string {
  const header = ['requestId', 'customerName', 'agentName', 'startedAt', 'endedAt', 'durationSec', 'status', 'issueSummary'];
  const lines = rows.map((r) =>
    [
      r.requestId,
      JSON.stringify(r.customerName ?? ''),
      JSON.stringify(r.agentName),
      new Date(r.startedAt).toISOString(),
      new Date(r.endedAt).toISOString(),
      r.durationSec,
      r.status,
      JSON.stringify(r.issueSummary),
    ].join(','),
  );
  return [header.join(','), ...lines].join('\n');
}
